import { submitReview } from '@/api/widget'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { getCurrentGlobalID, getCurrentMenuType, useSessionStore } from './useSessionStore'
import { getStoreIDByGlobalID } from './useLocationsStore'
import { ProductView, SubmitReviewRequest, SubmitReviewResponse, UserReviewForm } from '@/types/widget'
import { Setter } from '@/types'
import { createPersistOptions } from '@/utils/zustand-persist'
import { showErrorToast } from '@/utils'
import { track } from '@/utils/analytics'
import { classifyReviewError, reviewErrorMessage } from '@/utils/reviewErrors'
import { withRefreshRetry } from '@/utils/reviewAuth'

type SubmitReviewStoreState = {
  loading: boolean
  error: string | undefined
  success: boolean

  product: ProductView | undefined
  form: UserReviewForm | undefined
  response: SubmitReviewResponse | undefined
}

type SubmitReviewStoreActions = {
  setProduct: (product: ProductView) => void
  setForm: (form: Setter<UserReviewForm>) => void
  submitReview: () => Promise<SubmitReviewResponse | undefined>
  reset: () => void
}

type SubmitReviewStore = SubmitReviewStoreState & SubmitReviewStoreActions

const initialState: SubmitReviewStoreState = {
  loading: false,
  error: undefined,
  success: false,
  product: undefined,
  form: undefined,
  response: undefined,
}

export const useSubmitReviewStore = create<SubmitReviewStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setProduct: (product: ProductView) => set({ ...initialState, product }, false, 'setProduct'),
        setForm: (form: Setter<UserReviewForm>) => {
          const currentForm = get().form
          const newForm = typeof form === 'function' ? form(currentForm ?? ({} as UserReviewForm)) : form
          set({ form: newForm as UserReviewForm }, false, 'setForm')
        },

        submitReview: async () => {
          set({ loading: true, error: undefined }, false, 'submitReview/start')
          const { product, form } = get()
          if (!product || !form) {
            set({ error: 'No request body set', loading: false }, false, 'submitReview/error')
            return undefined
          }
          const { currentUID, reviewToken, reviewPII } = useSessionStore.getState()
          if (!currentUID) {
            set({ error: 'No current UID set', loading: false }, false, 'submitReview/error')
            return undefined
          }
          if (!reviewToken || !reviewPII) {
            set(
              { error: 'Please sign in to submit a review', loading: false },
              false,
              'submitReview/error'
            )
            return undefined
          }

          // Rating is always required. Potency is optional (the step is skippable
          // and can be disabled), so only validate it when the user provided one.
          const rating = Math.round(Number(form.rating))
          if (!Number.isFinite(rating) || rating < 1 || rating > 5) {
            const msg = 'Please rate the product before submitting.'
            set({ error: msg, loading: false }, false, 'submitReview/error')
            showErrorToast(msg)
            return undefined
          }

          const potency = form.potency == null ? undefined : Math.round(Number(form.potency))
          if (potency !== undefined && (!Number.isFinite(potency) || potency < 1 || potency > 5)) {
            const msg = 'Please select a potency before submitting.'
            set({ error: msg, loading: false }, false, 'submitReview/error')
            showErrorToast(msg)
            return undefined
          }

          const payload: SubmitReviewRequest = {
            productId: product.product_uid,
            rating,
            effects: form.effects ?? [],
            positiveExperiences: form.positiveExperiences ?? [],
            negativeExperiences: form.negativeExperiences ?? [],
            reviewComment: form.reviewComment?.trim() ? form.reviewComment.trim() : null,
            ...(potency !== undefined ? { potency } : {}),
          }

          try {
            const globalID = getCurrentGlobalID()
            const [response, error] = await withRefreshRetry((tok) =>
              submitReview(currentUID, reviewPII, tok, payload, {
                storeID: globalID ? getStoreIDByGlobalID(globalID) : undefined,
                menuType: getCurrentMenuType(),
                globalId: globalID,
              })
            )
            if (error) {
              set({ error, loading: false }, false, 'submitReview/error')
              showErrorToast(reviewErrorMessage(error))
              if (classifyReviewError(error) === 'contactNotFound') {
                useSessionStore.getState().clearReviewAuth()
              }
              return undefined
            }

            set({ success: true, loading: false, response }, false, 'submitReview/success')
            return response
          } finally {
            track('review', 'create_review', { productName: product.strain })
          }
        },
        reset: () => set(initialState, false, 'reset'),
      }),
      createPersistOptions<SubmitReviewStore, 'product' | 'form' | 'success' | 'response' | 'error'>({
        name: 'submit-review',
        pick: ['product', 'form', 'success', 'response', 'error'],
        storageType: 'session',
      })
    ),
    {
      name: 'SubmitReviewStore',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
)

/**
 * Get the review form (used by the isComplete predicates in screen-utils).
 */
export function getReviewForm(): UserReviewForm | undefined {
  return useSubmitReviewStore.getState().form
}
